import type { ValueSetExpandParams } from '@medplum/core'
import { type JSX, type ReactNode, useCallback, useMemo } from 'react'
import {
  ComboboxAsync,
  type ComboboxAsyncOption,
} from '#src/components/core/combobox/ComboboxAsync.tsx'
import {
  defaultToOption,
  type ValueSetAutocompleteValue,
} from '#src/components/ValueSetAutocomplete/toOptions.ts'
import { valueSetAutocompleteLoadOptions } from '#src/components/ValueSetAutocomplete/valueSetAutocompleteLoadOptions.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'

interface ValueSetAutocompleteProps {
  // A single ValueSet URL, or several to union. Multiple bindings are expanded
  // independently (each with `countPerBinding`) and merged, so no source can
  // crowd another out of the result window before ranking.
  readonly binding: string | string[]
  readonly countPerBinding?: number
  readonly values?: ValueSetAutocompleteValue[]
  readonly expandParams?: Partial<ValueSetExpandParams>
  readonly toOption?: (
    element: ValueSetAutocompleteValue
  ) => ComboboxAsyncOption<ValueSetAutocompleteValue>
  readonly children: ReactNode
  readonly onSelect: (value: ValueSetAutocompleteValue | null) => void
  initialOpen?: boolean
}

export function ValueSetAutocomplete({
  binding,
  countPerBinding,
  values,
  expandParams,
  toOption = defaultToOption,
  children,
  onSelect,
  initialOpen = false,
}: ValueSetAutocompleteProps): JSX.Element {
  const medplum = useMedplum()

  const bindings = useMemo(
    () => (Array.isArray(binding) ? binding : [binding]),
    [binding]
  )

  const loadOptions = useCallback(
    async ({ input, signal }: { input: string; signal: AbortSignal }) =>
      valueSetAutocompleteLoadOptions({
        input,
        signal,
        medplum,
        expandParams: expandParams ?? {},
        binding: bindings,
        countPerBinding,
        toOption,
      }),
    [medplum, expandParams, bindings, countPerBinding, toOption]
  )

  return (
    <ComboboxAsync
      values={values}
      queryKey={['value-set-autocomplete', ...bindings]}
      loadOptions={loadOptions}
      onSelect={onSelect}
      // Identify options by system+code to match the loader's dedup key, so a
      // numeric code shared across terminologies (e.g. a merged SNOMED+RxNorm
      // result) isn't collapsed back to one entry at the display layer.
      getValue={(o) => (o.code ? `${o.system}|${o.code}` : '')}
      initialOpen={initialOpen}
      // Merged multi-source results are all server-validated matches; let Fuse
      // rank them without dropping any.
      rankOnly={bindings.length > 1}
    >
      {children}
    </ComboboxAsync>
  )
}
